// src/pages/WhitepaperPage.js
import React from "react";
import { Helmet } from "react-helmet-async";
import "./WhitepaperPage.css";

function WhitepaperPage() {
  const whitepaperSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://trendgenie.io/whitepaper",
    },
    headline: "Trend Genie & TGEN Token Whitepaper",
    description:
      "Official whitepaper for the Trend Genie ecosystem and the TGEN utility token on BNB Smart Chain.",
    about: {
      "@type": "Organization",
      name: "Trend Genie",
      url: "https://trendgenie.io",
    },
    publisher: {
      "@type": "Organization",
      name: "Trend Genie",
      logo: {
        "@type": "ImageObject",
        url: "https://trendgenie.io/assets/logo.png",
      },
    },
  };

  const sections = [
    { id: "intro", label: "1. Introduction" },
    { id: "vision", label: "2. Vision" },
    { id: "products", label: "3. Current Products" },
    { id: "problem", label: "4. The Problem" },
    { id: "approach", label: "5. Our Approach" },
    { id: "token-overview", label: "6. TGEN Token Overview" },
    { id: "access-model", label: "7. Access Model" },
    { id: "token-utility", label: "8. Token Utility Summary" },
    { id: "token-economics", label: "9. Token Economics" },
    { id: "roadmap", label: "10. Roadmap" },
    { id: "risks", label: "11. Risks & Limitations" },
    { id: "summary", label: "12. Summary" },
  ];

  return (
    <div className="page whitepaper-page">
      <Helmet>
        <title>Trend Genie & $TGEN Token | Whitepaper</title>
        <meta
          name="description"
          content="Read the Trend Genie & TGEN token whitepaper. Learn how the AI trading app, RugShield scanner, and $TGEN utility token work together to make crypto trading safer and more educational."
        />
        <script type="application/ld+json">
          {JSON.stringify(whitepaperSchema)}
        </script>
      </Helmet>

      {/* HERO */}
      <section className="whitepaper-hero">
        <div className="whitepaper-hero-inner">
          <div className="whitepaper-hero-left">
            <p className="whitepaper-hero-kicker">Whitepaper · 1.0</p>
            <h1>Trend Genie, RugShield &amp; $TGEN Token</h1>
            <p className="whitepaper-hero-subtitle">
              The official overview of the Trend Genie ecosystem, RugShield
              scanner, and the $TGEN utility token on BNB Smart Chain.
            </p>

            <div className="whitepaper-hero-cta-row">
              <a
                href="/trendgenie-whitepaper.pdf"
                className="whitepaper-hero-btn"
                target="_blank"
                rel="noreferrer"
              >
                Download PDF
              </a>
              <a
                href="https://bscscan.com/token/PASTE_TGEN_TOKEN_ADDRESS"
                className="whitepaper-hero-link"
                target="_blank"
                rel="noreferrer"
              >
                View TGEN on BscScan
              </a>
            </div>

            <div className="whitepaper-disclaimer">
              <h3>Disclaimer</h3>
              <p>
                Trend Genie and TGEN are tools for market analysis and crypto
                safety education. Nothing in this document is financial advice.
                Trading and investing in cryptocurrencies involves significant
                risk, including possible loss of capital. Always do your own
                research.
              </p>
            </div>
          </div>

          <aside className="whitepaper-hero-right">
            <div className="whitepaper-token-card">
              <h3>TGEN Token Snapshot</h3>
              <ul>
                <li>
                  <span>Token Name</span>
                  <strong>Trend Genie</strong>
                </li>
                <li>
                  <span>Ticker</span>
                  <strong>$TGEN</strong>
                </li>
                <li>
                  <span>Network</span>
                  <strong>BNB Smart Chain (BEP-20)</strong>
                </li>
                <li>
                  <span>Totaly Supply</span>
                  <strong>500M</strong>
                </li>
                <li>
                  <span>Role</span>
                  <strong>Utility &amp; access token</strong>
                </li>
              </ul>
              <p>
                $TGEN is a utility token used for access, usage limits and
                future governance across Trend Genie tools. It does not
                represent equity or a right to profits.
              </p>
            </div>
          </aside>
        </div>
      </section>

      {/* MAIN CONTENT WRAPPER WITH TOC + BODY */}
      <section className="whitepaper-main">
        {/* TOC */}
        <nav className="whitepaper-toc">
          <h2>Contents</h2>
          <ul>
            {sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`}>{s.label}</a>
              </li>
            ))}
          </ul>
        </nav>

        {/* BODY */}
        <article className="whitepaper-body">
          {/* 1. Introduction */}
          <section id="intro" className="whitepaper-section">
            <h2>1. Introduction</h2>
            <p>
              Crypto moves fast, scams move faster, and most new traders are
              stuck between two extremes:
            </p>
            <ul>
              <li>Random meme coins with no fundamentals</li>
              <li>Overcomplicated tools that assume you are already a pro</li>
            </ul>
            <p>
              Trend Genie was created for the middle ground. Practical tools
              that help normal people read the market, understand risk, and
              build better habits without promising{" "}
              <span className="nowrap">“guaranteed gains”.</span>
            </p>
            <p>We combine:</p>
            <ul>
              <li>
                An AI powered trading analysis app for crypto (and later,
                stocks)
              </li>
              <li>
                A DeFi safety scanner for Ethereum and BNB Chain that flags
                obvious rug pulls, honeypots, and ownership risks
              </li>
            </ul>
            <p>
              Now we are adding the TGEN utility token, built on BNB Smart
              Chain (BEP-20), to connect access across all Trend Genie tools and
              give power users a simple way to unlock more features.
            </p>
          </section>

          {/* 2. Vision */}
          <section id="vision" className="whitepaper-section">
            <h2>2. Vision</h2>
            <p>
              Our long term vision is simple: make web3 and trading tools safer,
              smarter, and more accessible – especially for students, newcomers,
              and small retail traders.
            </p>
            <p>
              TGEN is not designed to be a “get rich quick” scheme. It is:
            </p>
            <ul>
              <li>A utility and access token for using the Trend Genie ecosystem</li>
              <li>
                A way to reward long term users who actively learn and engage
              </li>
              <li>
                A foundation for a future community driven roadmap (governance,
                feature votes, and more)
              </li>
            </ul>
          </section>

          {/* 3. Current Products */}
          <section id="products" className="whitepaper-section">
            <h2>3. Current Products</h2>

            <h3>3.1 Trend Genie App – AI Crypto Signals</h3>
            <p>The Trend Genie Android app helps traders:</p>
            <ul>
              <li>
                Track the market with a crypto heatmap and market overview
              </li>
              <li>
                Get AI assisted trading signals for major coins (BTC, ETH, and
                top altcoins)
              </li>
              <li>
                See context behind each signal: key indicators, patterns, and
                risk notes
              </li>
              <li>
                Build watchlists, set alerts, and keep an eye on potential
                breakout setups
              </li>
            </ul>
            <p>The app is designed for both:</p>
            <ul>
              <li>
                New traders – who need simple explanations and basic risk
                awareness
              </li>
              <li>
                Experienced traders – who want faster market scanning and a
                second opinion
              </li>
            </ul>
            <p>
              We focus on explanations and structure, not hype. The goal is to
              support your own decisions, not replace them.
            </p>

            <h3>3.2 Trend Genie RugShield – DeFi Safety Scanner</h3>
            <p>
              On the web (and soon in app), Trend Genie RugShield lets users
              paste any ERC-20 or BEP-20 token contract and get an instant risk
              snapshot:
            </p>
            <ul>
              <li>Honeypot checks (can you actually sell the token?)</li>
              <li>Ownership and liquidity red flags</li>
              <li>Simple traffic light style output (green / yellow / red)</li>
            </ul>
            <p>Two explanation modes are available:</p>
            <ul>
              <li>Beginner mode – plain English</li>
              <li>Pro mode – more technical details</li>
            </ul>
            <p>
              RugShield uses only read only on chain data and external risk
              APIs. It never asks for private keys or permission to move funds.
            </p>
          </section>

          {/* 4. The Problem */}
          <section id="problem" className="whitepaper-section">
            <h2>4. The Problem</h2>
            <p>
              The typical new trader faces three problems at the same time:
            </p>
            <h3>Information overload</h3>
            <p>
              Hundreds of metrics, charts, indicators, and tools – but no clear
              framework to turn it into decisions.
            </p>
            <h3>Security blind spots</h3>
            <p>
              Many people can read price charts, but cannot read smart contracts
              or ownership patterns – making them easy targets for rug pulls and
              honeypots.
            </p>
            <h3>Paywalls and monthly subscriptions</h3>
            <p>
              Most tools lock everything behind a fixed monthly fee, which is
              hard to justify for students or small accounts.
            </p>
            <p>
              We want to keep core education and basic safety checks accessible,
              while still having a sustainable model for advanced features and
              infrastructure costs.
            </p>
          </section>

          {/* 5. Our Approach */}
          <section id="approach" className="whitepaper-section">
            <h2>5. Our Approach</h2>
            <p>Trend Genie is built on three principles:</p>
            <h3>Education first</h3>
            <p>
              Tools must teach you why something looks risky or interesting, not
              just flash “BUY/SELL” signals.
            </p>
            <h3>Security by default</h3>
            <p>
              Read only checks, clear risk flags, no connection to your funds,
              and no API keys required for the basic experience.
            </p>
            <h3>Token gated power features, not basic access</h3>
            <p>
              Anyone should be able to try the core tools. Holding TGEN simply
              unlocks more depth, frequency, and convenience.
            </p>
          </section>

          {/* 6. Token Overview */}
          <section id="token-overview" className="whitepaper-section">
            <h2>6. TGEN Token Overview</h2>
            <div className="whitepaper-token-grid">
              <div>
                <p>
                  <strong>Name:</strong> Trend Genie
                </p>
                <p>
                  <strong>Ticker:</strong> TGEN
                </p>
                <p>
                  <strong>Standard:</strong> BEP-20
                </p>
                <p>
                  <strong>Network:</strong> BNB Smart Chain
                </p>
              </div>
              <div>
                <p>
                  <strong>Role:</strong> Utility token for access, limits and
                  future governance across Trend Genie tools.
                </p>
                <p>
                  TGEN’s primary purpose is to:
                </p>
                <ul>
                  <li>
                    Unlock higher usage limits and advanced features in Trend
                    Genie apps
                  </li>
                  <li>
                    Align the most engaged users with the long term growth of
                    the ecosystem
                  </li>
                  <li>
                    Provide a simple on chain way to verify access without
                    usernames or passwords
                  </li>
                </ul>
              </div>
            </div>
            <p>
              Important: TGEN does not represent equity, ownership in the
              company, or a right to profits or revenue. It is a digital utility
              asset used within the Trend Genie ecosystem.
            </p>
          </section>

          {/* 7. Access Model */}
          <section id="access-model" className="whitepaper-section">
            <h2>7. Access Model: How TGEN Integrates With the Tool</h2>

            <h3>7.1 Free Tier – Open Education</h3>
            <p>We never want to fully lock out people who are still learning.</p>
            <p>For non holders (or wallets below a minimum threshold):</p>
            <ul>
              <li>Access to the scanner and basic tools is free but rate limited</li>
              <li>up to 3 scans per day on RugShield</li>
              <li>Limited access to AI signal explanations or watchlist size</li>
            </ul>
            <p>
              Purpose: let new users learn, experiment, and test the tool before
              committing.
            </p>

            <h3>7.2 Holder Tiers – Token Gated Power Mode</h3>
            <p>
              Holding TGEN in your wallet unlocks more usage. Numbers can be
              adjusted later, but a sample structure looks like:
            </p>
            <div className="whitepaper-tier-grid">
              <div className="whitepaper-tier-card">
                <h4>Tier 0 – Guest</h4>
                <ul>
                  <li>Balance: 0 TGEN</li>
                  <li>3 free scans per day</li>
                  <li>Limited AI signals</li>
                </ul>
              </div>
              <div className="whitepaper-tier-card">
                <h4>Tier 1 – Explorer</h4>
                <ul>
                  <li>Balance: ≥ 1,000 TGEN</li>
                  <li>30 scans per day</li>
                  <li>Extended explanations and more metrics</li>
                  <li>Higher daily usage of app features</li>
                </ul>
              </div>
              <div className="whitepaper-tier-card">
                <h4>Tier 2 – Pro</h4>
                <ul>
                  <li>Balance: ≥ 10,000 TGEN</li>
                  <li>Effectively unlimited scans (fair use)</li>
                  <li>Priority queue for scanner requests</li>
                  <li>Early access to new indicators and experimental features</li>
                  <li>Deeper analytics in the mobile app</li>
                </ul>
              </div>
              <div className="whitepaper-tier-card">
                <h4>Tier 3 – Partner</h4>
                <ul>
                  <li>Balance: ≥ 50,000 TGEN</li>
                  <li>All Pro benefits</li>
                  <li>Private beta features</li>
                  <li>
                    Eligibility for future governance / advisory roles (when
                    launched)
                  </li>
                </ul>
              </div>
            </div>
            

            <h3>7.3 How the Check Works (High Level)</h3>
            <p>At a high level:</p>
            <ul>
              <li>Users connect or sign in with a wallet on app.trendgenie.io</li>
              <li>The backend checks the wallet address and TGEN balance</li>
              <li>A tier is assigned based on that balance</li>
            </ul>
            <p>Every API call (scan, signal, etc.) then checks:</p>
            <ul>
              <li>Today’s usage count for that wallet</li>
              <li>The tier limits</li>
            </ul>
            <p>
              If within limits → request allowed. If limit exceeded → user sees
              a friendly message suggesting TGEN holding to unlock more.
            </p>
            <p>
              No private keys are ever stored; only public address and usage
              counters.
            </p>
          </section>

          {/* 8. Token Utility Summary */}
          <section id="token-utility" className="whitepaper-section">
            <h2>8. Token Utility Summary</h2>
            <p>TGEN is designed to power several utilities:</p>
            <ul>
              <li>
                <strong>Usage &amp; Rate Limits:</strong> Higher tiers mean more
                scans, more signals, more alerts.
              </li>
              <li>
                <strong>Advanced Features:</strong> Access to additional
                metrics, new chains, premium risk checks, and deeper AI
                explanations.
              </li>
              <li>
                <strong>Early Access &amp; Beta Testing:</strong> TGEN holders
                (especially higher tiers) can test new modules before public
                release and give feedback.
              </li>
              <li>
                <strong>Future Governance (Planned):</strong> Non binding
                community votes on supported chains, new metrics, and tier
                adjustments.
              </li>
              <li>
                <strong>Ecosystem Discounts (Planned):</strong> If paid fiat
                subscriptions are ever added, TGEN holders may receive
                discounts or bundled perks.
              </li>
            </ul>
          </section>

          {/* 9. Token Economics */}
          <section id="token-economics" className="whitepaper-section">
            <h2>9. Token Economics (High Level)</h2>
            <p>
              Detailed tokenomics (exact numbers, vesting schedules and
              distribution addresses) will be published in a separate
              “Tokenomics” document. This section explains the guiding
              principles only.
            </p>

            <h3>9.1 Fixed Supply</h3>
            <p>
              TGEN is designed as a fixed supply token. No new tokens will be
              minted after deployment, or minting is permanently disabled /
              ownership renounced once initial distribution is complete.
            </p>

            <h3>9.2 Allocation Framework</h3>
            
            <ul>
              <li>
                <strong>Liquidity &amp; Market Making – 40%</strong> – Used to
                seed pools and ensure reasonable trading depth.
              </li>
              <li>
                <strong>Community &amp; Rewards – 25%</strong> – Airdrops,
                giveaways, learning quests, bug bounties, educational campaigns.
              </li>
              <li>
                <strong>Ecosystem &amp; Development Treasury – 25%</strong> –
                Infrastructure costs (RPC, indexing, servers), R&amp;D, audits,
                new features.
              </li>
              <li>
                <strong>Team &amp; Long Term Contributors – 10%</strong> –
                Vested over multiple years to align incentives with long term
                growth.
              </li>
            </ul>
            <p>
              Any final allocation and vesting details should be transparent and
              publicly documented, with addresses visible on BscScan.
            </p>
          </section>

          {/* 10. Roadmap */}
          <section id="roadmap" className="whitepaper-section">
            <h2>10. Roadmap (High Level)</h2>
           
            <div className="whitepaper-roadmap-grid">
              <div className="whitepaper-roadmap-card">
                <h3>Phase 1 – Foundations (Done / In Progress)</h3>
                <ul>
                  <li>Launch Trend Genie Android app (AI crypto signals) Launched on Google Play</li>
                  <li>
                    Launch RugShield web scanner for Ethereum &amp; BNB Chain (Launched app.trendgenie.io)
                  </li>
                  <li>Deploy TGEN BEP-20 token</li>
                  <li>Publish whitepaper &amp; core docs (Published)</li>
                </ul>
              </div>
              <div className="whitepaper-roadmap-card">
                <h3>Phase 2 – Token Integration</h3>
                <ul>
                  <li>Add wallet based login to app.trendgenie.io</li>
                  <li>
                    Implement TGEN based usage tiers (as described above)
                  </li>
                  <li>
                    Add TGEN holder perks inside the RugShield app (e.g., more
                    watchlist slots, more detailed AI breakdowns)
                  </li>
                  <li>
                    Launch initial TGEN/BNB liquidity pool
                  </li>
                </ul>
              </div>
              <div className="whitepaper-roadmap-card">
                <h3>Phase 3 – Pro Features &amp; Expansion</h3>
                <ul>
                  <li>
                    Add support for more chains or token standards (depending on
                    demand)
                  </li>
                  <li>
                    Deeper on chain analytics: liquidity locking, time based
                    unlocks, deployer history
                  </li>
                  <li>
                    Expanded AI modules: portfolio stress testing, scenario
                    analysis, and risk education flows
                  </li>
                  <li>Start community proposals / voting on new features</li>
                </ul>
              </div>
              <div className="whitepaper-roadmap-card">
                <h3>Phase 4 – Ecosystem &amp; Governance</h3>
                <ul>
                  <li>
                    Formalize governance mechanisms for TGEN holders (advisory
                    votes, feature polls)
                  </li>
                  <li>
                    Explore integration with other DeFi protocols or wallets
                  </li>
                  <li>
                    Launch partner program for educators, research groups and
                    security communities
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* 11. Risks */}
          <section id="risks" className="whitepaper-section">
            <h2>11. Risks &amp; Limitations</h2>
            <p>
              It is important to be clear about what Trend Genie and TGEN
              cannot guarantee:
            </p>
            <ul>
              <li>
                <strong>No guaranteed profit.</strong> AI signals and risk
                checks are decision support tools, not financial advice.
              </li>
              <li>
                <strong>No perfect security detection.</strong> RugShield can
                highlight many obvious red flags but cannot guarantee that a
                token is safe.
              </li>
              <li>
                <strong>Smart contract &amp; infrastructure risk.</strong> Bugs,
                RPC outages, or third party API issues may impact uptime or data
                accuracy.
              </li>
              <li>
                <strong>Regulatory uncertainty.</strong> Crypto regulations
                continue to evolve across jurisdictions. Trend Genie will adapt
                features and availability as needed to comply with relevant
                laws.
              </li>
              <li>
                <strong>Market volatility.</strong> The price of TGEN and other
                crypto assets can be extremely volatile. Access to tools should
                never be the reason to over expose yourself financially.
              </li>
            </ul>
          </section>

          {/* 12. Summary */}
          <section id="summary" className="whitepaper-section">
            <h2>12. Summary</h2>
            <p>
              Trend Genie exists to make crypto trading and DeFi exploration
              safer, clearer, and more educational for normal people – not just
              full time quants.
            </p>
            <ul>
              <li>
                The Trend Genie app gives AI assisted market views, signals and
                explanations.
              </li>
              <li>
                The RugShield scanner helps users avoid obvious honeypots and
                risky contracts.
              </li>
              <li>
                The TGEN token connects everything together, unlocking higher
                usage, deeper insights and future governance, while keeping
                basic education and safety checks accessible to everyone.
              </li>
            </ul>
            <p>
              If you are a builder, educator, trader or security researcher and
              want to collaborate, you can reach the team at{" "}
              <a href="mailto:support@trendgenie.io">support@trendgenie.io</a>{" "}
              or via the links on{" "}
              <a href="https://trendgenie.io" target="_blank" rel="noreferrer">
                trendgenie.io
              </a>
              .
            </p>
          </section>
        </article>
      </section>
    </div>
  );
}

export default WhitepaperPage;
